import { useCreateArtwork } from "@/api/artwork/artwork";
import {
  appendNoDuplicateRecordingNumbersRefine,
  artworkSchema,
} from "@/api/artwork/artwork-validation";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { toast } from "@/hooks/use-toast";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

type FormType = {
  id?: string;
  oeuvresDescriptionNumeroEnregistrement: string;
  typeOeuvre: string;
};

type NewArtworkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewArtworkDialog = ({ onOpenChange, open }: NewArtworkDialogProps) => {
  const { t } = useTranslation();
  const mainLanguage = getMainLanguage() as SupportedLocale;
  const schema = useMemo(
    () =>
      appendNoDuplicateRecordingNumbersRefine(
        artworkSchema
          .pick({
            id: true,
            oeuvresDescriptionNumeroEnregistrement: true,
          })
          .extend({
            typeOeuvre: z.string(),
          }),
      ),
    [],
  );
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      oeuvresDescriptionNumeroEnregistrement: "",
      typeOeuvre: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const { mutateAsync, isPending } = useCreateArtwork();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      form.reset({
        oeuvresDescriptionNumeroEnregistrement: "",
        typeOeuvre: "",
      });
    }
  }, [open, form]);

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync({
        oeuvresDescriptionNumeroEnregistrement:
          data.oeuvresDescriptionNumeroEnregistrement,
        ...(data.typeOeuvre.trim()
          ? {
              oeuvresDescriptionTypeOeuvreMultilingual: {
                [mainLanguage]: data.typeOeuvre.trim(),
              },
            }
          : {}),
      });
      navigate(`/edit/artwork/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("oeuvresDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 403) {
        toast({
          title: t("maxArtworksLimitReached"),
          duration: 3000,
          variant: "error",
        });
      }
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createAartwork")}
      onFormSubmit={onFormSubmit}
      form={form}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="oeuvresDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t("oeuvresDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <ModifiableList
        name="typeOeuvre"
        label={t("oeuvresDescriptionTypeOeuvreMultilingual")}
        listKey="liste-types-oeuvres"
        lang={mainLanguage}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewArtworkDialog;
